import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Hash, History, Package, Plus, Search, Sparkles, X } from 'lucide-react';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { useIsPhone } from '@/hooks/useIsPhone';
import { PageHeader } from '@/app/DashboardLayout';
import { DraftRowActions, DraftsFilter, useDecideDraft, useDraftCount, type CatalogueFilter } from '@/features/common/Drafts';
import { HsnSacField } from '@/features/common/HsnSacField';
import { ApplyHsnSheet } from './ApplyHsnSheet';
import {
  Badge, Button, Card, CardLine, CardRow, Cards, EmptyState, ErrorState, Input, Money, SkeletonTable, Textarea,
  TBody, TD, TH, THead, TR, Table,
  Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader,
} from '@/features/common/ui';

interface ItemRow {
  id: string;
  name: string;
  sku: string | null;
  type: 'goods' | 'service';
  rate: string | null;
  unit: string | null;
  taxId: string | null;
  taxName: string | null;
  hsnSac: string | null;
  isActive: boolean;
  /** 'draft' when the AI agent proposed this row and nobody has confirmed it. See Drafts.tsx. */
  status?: 'active' | 'draft';
  isDraft?: boolean;
  proposalNote?: string | null;
}
interface TaxOption { id: string; name: string }

interface ItemVersion {
  id: string;
  version: number;
  name: string;
  rate: string | null;
  unit: string | null;
  hsnSac: string | null;
  taxName: string | null;
  createdAt: string;
  createdByName: string | null;
  isCurrent: boolean;
  linesBilled: number;
}

interface BilledSuggestion {
  name: string;
  rate: string;
  hsnSac: string | null;
  unit: string | null;
  taxId: string | null;
  timesBilled: number;
  lastBilledOn: string;
}

const dateLabel = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

export function ItemsPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const isPhone = useIsPhone();
  // A search result opens this list already narrowed to it (`?q=`).
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('q') ?? '');
  const [composerOpen, setComposerOpen] = useState(false);
  /** The item being edited, or a set of values to prefill a new one. */
  const [editing, setEditing] = useState<ItemRow | null>(null);
  const [prefill, setPrefill] = useState<Partial<ItemRow> | null>(null);
  /** The catalogue, or the drafts the AI agent proposed. */
  const [filter, setFilter] = useState<CatalogueFilter>('active');
  const draftCount = useDraftCount('items');
  const decide = useDecideDraft('item', 'items');
  // `/items?hsn=apply` - the Review page's "Apply codes" lands here with the sheet open.
  const [applyingHsn, setApplyingHsn] = useState(() => new URLSearchParams(window.location.search).get('hsn') === 'apply');

  // How many items have no HSN/SAC - the banner's number, and whether to
  // offer the sheet at all. Cheap, and the same key prefix as the list so a
  // save refreshes it.
  const hsnGaps = useQuery({
    queryKey: ['items', org?.id, 'hsn-gaps'],
    queryFn: () => api.get<{ items: Array<{ id: string }>; adhocLines: number; canFillLines: boolean }>('/items/hsn-gaps'),
    enabled: !!org?.id && perms.can('item', 'edit'),
  });
  const missingCodes = hsnGaps.data?.items.length ?? 0;

  const query = useQuery({
    queryKey: ['items', org?.id, search, filter],
    // The default list is active rows only - the server's default too - and
    // a draft is listed only when asked for by name.
    queryFn: () => api.get<{ items: ItemRow[] }>(`/items${qs({ q: search, status: filter === 'draft' ? 'draft' : undefined })}`),
    enabled: !!org?.id,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['items', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['recently-billed', org?.id] });
    // Any save can mint a version, restoring one certainly does - and without
    // this the history is served from cache the next time the sheet is opened,
    // so a restore appears to have done nothing.
    void queryClient.invalidateQueries({ queryKey: ['item-versions'] });
  };

  const closeSheet = () => {
    setComposerOpen(false);
    setEditing(null);
    setPrefill(null);
  };

  const openNew = (values?: Partial<ItemRow>) => {
    setEditing(null);
    setPrefill(values ?? null);
    setComposerOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Items"
        subtitle="What you sell, so an invoice line is one click rather than four fields."
        actions={
          perms.can('item', 'create') ? (
            <Button size="sm" onClick={() => openNew()}>
              <Plus className="size-4" aria-hidden />
              New item
            </Button>
          ) : null
        }
      />

      {applyingHsn ? <ApplyHsnSheet onClose={() => setApplyingHsn(false)} /> : null}

      <div className="space-y-4 px-5 pb-8">
        {missingCodes > 0 ? (
          /* The Review says this one item at a time; here it is one press.
             Only while there is something to fix - a banner that is always
             there is furniture. */
          <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm dark:border-amber-500/30 dark:bg-amber-500/10"
            data-testid="hsn-gaps-banner"
          >
            <p className="flex items-center gap-2">
              <Hash className="size-4 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden />
              {missingCodes === 1 ? '1 item has no HSN/SAC code.' : `${missingCodes} items have no HSN/SAC code.`}{' '}
              <span className="text-muted-foreground">GST invoices need one on every line.</span>
            </p>
            <Button type="button" size="sm" variant="outline" onClick={() => setApplyingHsn(true)}>
              Apply codes
            </Button>
          </div>
        ) : null}
        <div className="relative max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            className="pl-8"
            placeholder="Search by name, SKU or HSN"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search items"
          />
        </div>

        {/* Only when not searching: a suggestion strip on top of results reads
            as a result, and the one thing it must never be is confusable with
            what was asked for. */}
        {!search && filter === 'active' && perms.can('item', 'create') ? <RecentlyBilled onAdd={openNew} /> : null}

        <DraftsFilter value={filter} count={draftCount} onChange={setFilter} noun="items" />

        <Card>
          {query.isError ? (
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          ) : query.isLoading ? (
            <SkeletonTable rows={5} cols={4} />
          ) : query.data?.items.length ? (
            isPhone ? (
              <Cards>
                {query.data.items.map((item) => (
                  <CardRow key={item.id} data-testid="item-row" data-draft={item.isDraft ? 'true' : undefined}>
                    <button
                      type="button"
                      className="block w-full text-left"
                      onClick={() => {
                        setEditing(item);
                        setComposerOpen(true);
                      }}
                    >
                      <CardLine
                        title={
                          <span className="flex items-center gap-2">
                            <span className="truncate">{item.name}</span>
                            {item.isDraft ? <Badge variant="warning">Draft</Badge> : !item.isActive ? <Badge>Inactive</Badge> : null}
                          </span>
                        }
                        meta={[item.sku ? `SKU ${item.sku}` : null, item.hsnSac ? `HSN ${item.hsnSac}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                        value={item.rate ? <Money value={item.rate} currency="INR" /> : '—'}
                        sub={item.unit ? `per ${item.unit}` : (item.taxName ?? undefined)}
                      />
                    </button>
                    {item.isDraft ? <DraftRowActions id={item.id} name={item.name} decide={decide} note={item.proposalNote} compact /> : null}
                  </CardRow>
                ))}
              </Cards>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Name</TH>
                    <TH>Type</TH>
                    <TH>HSN/SAC</TH>
                    <TH>Tax</TH>
                    <TH numeric>Rate</TH>
                  </TR>
                </THead>
                <TBody>
                  {query.data.items.map((item) => (
                    <TR
                      key={item.id}
                      className="cursor-pointer"
                      data-testid="item-row"
                      data-draft={item.isDraft ? 'true' : undefined}
                      onClick={() => {
                        setEditing(item);
                        setComposerOpen(true);
                      }}
                    >
                      <TD>
                        <span className="inline-flex items-center gap-2">
                          <span className="font-medium">{item.name}</span>
                          {item.isDraft ? <Badge variant="warning">Draft</Badge> : null}
                        </span>
                        {item.sku ? <p className="text-xs text-muted-foreground">SKU {item.sku}</p> : null}
                        {item.isDraft ? (
                          // The row opens the editor on click; the buttons
                          // inside it must not. A confirm is not an edit.
                          <div onClick={(e) => e.stopPropagation()}>
                            <DraftRowActions id={item.id} name={item.name} decide={decide} note={item.proposalNote} />
                          </div>
                        ) : null}
                      </TD>
                      <TD>
                        <Badge variant={item.type === 'service' ? 'info' : 'neutral'}>
                          {item.type === 'service' ? 'Service' : 'Goods'}
                        </Badge>
                      </TD>
                      <TD className="text-muted-foreground">{item.hsnSac ?? '—'}</TD>
                      <TD className="text-muted-foreground">{item.taxName ?? '—'}</TD>
                      <TD numeric>
                        {item.rate ? (
                          <>
                            <Money value={item.rate} currency="INR" />
                            {item.unit ? <span className="text-muted-foreground"> / {item.unit}</span> : null}
                          </>
                        ) : (
                          '—'
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )
          ) : (
            <EmptyState
              icon={Package}
              title={filter === 'draft' ? 'No drafts waiting' : search ? 'Nothing matches that' : 'No items yet'}
              description={
                filter === 'draft'
                  ? 'When your AI agent proposes an item, it shows here - and in the Inbox - until you confirm it.'
                  : search
                    ? undefined
                    : 'Items are a convenience, not a requirement - an invoice line can be free text.'
              }
              action={
                filter !== 'draft' && !search && perms.can('item', 'create') ? (
                  <Button size="sm" onClick={() => openNew()}>
                    New item
                  </Button>
                ) : null
              }
            />
          )}
        </Card>
      </div>

      {composerOpen ? (
        <ItemComposer
          item={editing}
          prefill={prefill}
          onClose={closeSheet}
          onSaved={() => {
            closeSheet();
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * What has already been billed and is not in the catalogue.
 *
 * ── WHY THIS IS AN OFFER AND NOT A BACKGROUND JOB ──
 * Every free-typed invoice line is already a price this business charged, so
 * the history to build a catalogue from has been there all along - nothing
 * read it back. Bill "Website redesign, ₹50,000, HSN 998314" in March and by
 * April it is typed again from memory, with a different price and a different
 * HSN, and the two never reconcile on a return.
 *
 * Creating them automatically would be worse than the problem: half of what
 * goes on an invoice is a one-off ("Balance as agreed", "Consulting - phase one
 * for Acme"), and a catalogue full of those is a list somebody has to prune by
 * hand. So this shows what was billed most often and a person picks. It
 * disappears on its own once everything worth saving is saved.
 */
function RecentlyBilled({ onAdd }: { onAdd: (values: Partial<ItemRow>) => void }) {
  const org = useActiveOrg();
  const [dismissed, setDismissed] = useState<string[]>([]);

  const query = useQuery({
    queryKey: ['recently-billed', org?.id],
    queryFn: () => api.get<{ items: BilledSuggestion[] }>('/items/recently-billed'),
    enabled: !!org?.id,
  });

  const rows = (query.data?.items ?? []).filter((row) => !dismissed.includes(row.name)).slice(0, 4);
  if (!rows.length) return null;

  return (
    <Card className="border-primary/30 bg-primary/[0.03] p-4">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Sparkles className="size-4 text-primary" aria-hidden />
        Billed before, not saved yet
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Typed onto an invoice by hand. Save one and it is a click next time, with the same HSN.
      </p>
      <ul className="mt-3 space-y-2">
        {rows.map((row) => (
          <li
            key={row.name}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{row.name}</div>
              <div className="text-xs text-muted-foreground">
                <Money value={row.rate} currency="INR" />
                {row.hsnSac ? ` · HSN ${row.hsnSac}` : ''} · billed {row.timesBilled}
                {row.timesBilled === 1 ? ' time' : ' times'}, last on {dateLabel(row.lastBilledOn)}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  onAdd({
                    name: row.name,
                    rate: row.rate,
                    hsnSac: row.hsnSac,
                    unit: row.unit,
                    taxId: row.taxId,
                  })
                }
              >
                Save as item
              </Button>
              <Button
                size="icon"
                variant="ghost"
                aria-label={`Dismiss ${row.name}`}
                onClick={() => setDismissed((d) => [...d, row.name])}
              >
                <X className="size-4" aria-hidden />
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ItemComposer({
  item,
  prefill,
  onClose,
  onSaved,
}: {
  item: ItemRow | null;
  prefill: Partial<ItemRow> | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const perms = usePermissions();
  const editing = !!item;
  const source = item ?? prefill;

  const [form, setForm] = useState({
    name: source?.name ?? '',
    sku: source?.sku ?? '',
    type: (source?.type ?? 'service') as 'goods' | 'service',
    rate: source?.rate ?? '',
    unit: source?.unit ?? '',
    hsnSac: source?.hsnSac ?? '',
    taxId: source?.taxId ?? '',
    description: '',
  });
  const [showHistory, setShowHistory] = useState(false);

  const taxes = useQuery({
    queryKey: ['taxes', org?.id],
    queryFn: () => api.get<{ items: TaxOption[] }>('/taxes'),
    enabled: !!org?.id,
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        sku: form.sku || undefined,
        type: form.type,
        rate: form.rate || undefined,
        unit: form.unit || undefined,
        hsnSac: form.hsnSac || undefined,
        taxId: form.taxId || null,
        description: form.description || undefined,
      };
      return item ? api.patch(`/items/${item.id}`, body) : api.post('/items', body);
    },
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : undefined;
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const heading = editing ? 'Edit item' : 'New item';

  return (
    <Sheet label={heading} onClose={onClose}>
      <SheetHeader title={heading} onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          {/* Said before the fields, not after a save, because "will this
              change my invoices?" is the question somebody has while typing a
              new price - and the answer is reassuring. */}
          {editing && item?.isDraft ? (
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              A draft from your AI agent. It is in no picker and on no invoice until you confirm it - in the Inbox, or
              from the Drafts list.
            </p>
          ) : editing ? (
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              Invoices already raised keep the price they were raised at. A change here applies to the next document,
              and is kept in the history below.
            </p>
          ) : null}

          <Input label="Name" required autoFocus value={form.name} onChange={set('name')} error={error?.fieldError('name')} />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="item-type" className="text-sm font-medium">Type</label>
            <select
              id="item-type"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as 'goods' | 'service' }))}
              className="min-h-11 rounded-md border border-input bg-background px-3 text-base shadow-sm sm:min-h-9 sm:text-sm"
            >
              <option value="service">Service</option>
              <option value="goods">Goods</option>
            </select>
          </div>
          <Input label="Rate" inputMode="decimal" value={form.rate} onChange={set('rate')} error={error?.fieldError('rate')} />
          <Input label="Unit" value={form.unit} onChange={set('unit')} hint="hour, month, project, piece…" />
          <Input label="SKU" value={form.sku} onChange={set('sku')} error={error?.fieldError('sku')} />
          <HsnSacField
            value={form.hsnSac}
            onChange={(next) => setForm((f) => ({ ...f, hsnSac: next }))}
            nameForSuggestion={form.type === 'service' ? form.name : null}
            hint="Required on GST invoices. Type to search the SAC list; for goods, the HSN from the tariff."
            error={error?.fieldError('hsnSac')}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="item-tax" className="text-sm font-medium">Default tax</label>
            <select
              id="item-tax"
              value={form.taxId}
              onChange={(e) => setForm((f) => ({ ...f, taxId: e.target.value }))}
              className="min-h-11 rounded-md border border-input bg-background px-3 text-base shadow-sm sm:min-h-9 sm:text-sm"
            >
              <option value="">No tax</option>
              {taxes.data?.items.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <Textarea label="Description" value={form.description} onChange={set('description')} />

          {error && !error.errors?.length ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}

          {editing && item ? (
            <div className="border-t border-border pt-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory((s) => !s)}
                aria-expanded={showHistory}
              >
                <History className="size-4" aria-hidden />
                {showHistory ? 'Hide price history' : 'Price history'}
              </Button>
              {showHistory ? (
                <VersionHistory itemId={item.id} canRestore={perms.can('item', 'edit')} onRestored={onSaved} />
              ) : null}
            </div>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending}>{editing ? 'Save changes' : 'Add item'}</Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

/**
 * What this item has cost, and how much went out at each price.
 *
 * `linesBilled` is the figure that makes the list worth opening: "the rate
 * changed in April" is trivia, "and 34 invoices went out at the old one" is
 * something a person acts on.
 */
function VersionHistory({
  itemId,
  canRestore,
  onRestored,
}: {
  itemId: string;
  canRestore: boolean;
  onRestored: () => void;
}) {
  const query = useQuery({
    queryKey: ['item-versions', itemId],
    queryFn: () => api.get<{ items: ItemVersion[]; unversionedLines: number }>(`/items/${itemId}/versions`),
  });

  const restore = useMutation({
    mutationFn: (versionId: string) => api.post(`/items/${itemId}/versions/${versionId}/restore`),
    onSuccess: onRestored,
  });

  if (query.isLoading) return <SkeletonTable rows={2} cols={2} />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const unversioned = query.data?.unversionedLines ?? 0;

  return (
    <>
      {/* Named rather than folded into v1: those lines were raised before any
          history was kept, and attributing them to a price nobody can verify
          would be a confident wrong number next to a figure. */}
      {unversioned > 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {unversioned} earlier invoice {unversioned === 1 ? 'line' : 'lines'} came from this item, before price
          history was kept.
        </p>
      ) : null}
      <ol className="mt-3 space-y-2">
        {query.data?.items.map((version) => (
          <li key={version.id} className="rounded-md border border-border px-3 py-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className="truncate">{version.name}</span>
                  {version.isCurrent ? <Badge variant="info">Current</Badge> : null}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  v{version.version} · {dateLabel(version.createdAt)}
                  {version.createdByName ? ` · ${version.createdByName}` : ''}
                </div>
                {/* The figure that makes the list worth opening: "the rate
                    changed in April" is trivia, "and 34 lines went out at the
                    old one" is something a person acts on. */}
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {version.linesBilled === 0
                    ? 'Nothing billed at this price'
                    : `${version.linesBilled} invoice ${version.linesBilled === 1 ? 'line' : 'lines'} at this price`}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="tabular text-sm font-medium">
                  {version.rate ? <Money value={version.rate} currency="INR" /> : '—'}
                </div>
                {!version.isCurrent && canRestore ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    loading={restore.isPending}
                    onClick={() => restore.mutate(version.id)}
                  >
                    Restore
                  </Button>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </>
  );
}
